import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
 
// ❌ REMOVED wrong imports
// import { CheckCircle, Error, LocationOn } from "@mui/icons-material";
 
// ✅ Correct icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import MicIcon from "@mui/icons-material/Mic";
import WifiIcon from "@mui/icons-material/Wifi";
import SecurityIcon from "@mui/icons-material/Security";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReplayIcon from "@mui/icons-material/Replay";
 
import { Permission } from "../assets";
import { KycUserService } from "../Services/KycUserService";
import { auditLogService } from "../Services/AuditLogService";
import { geolocationService } from "../Services/GeolocationService";
 
 
const PermissionCheckScreen = ({ onPermissionsGranted }) => {
  const [permissions, setPermissions] = useState({
    camera: false,
    mic: false,
    bandwidth: false,
    vpn: false,
    location: false,
  });
 
  const [checked, setChecked] = useState({
    camera: false,
    mic: false,
    bandwidth: false,
    vpn: false,
    location: false,
  });
 
  const isMobile = useMediaQuery("(max-width:768px)");
  const session = localStorage.getItem("session");
 
  const [loadingKey, setLoadingKey] = useState(null);
  const [agree, setAgree] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [locationDialog, setLocationDialog] = useState(false);
  const [locationCheck, setLocationCheck] = useState({
    loading: false,
    result: null,
    error: null,
  });
 
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };
 
  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };
 
  const withTimeout = (promise, timeout = 30000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout")), timeout)
      ),
    ]);
 
    const [permissionDialog, setPermissionDialog] = useState({
      open: false,
      type: null, // "camera" | "mic"
    });
 
  const checkBandwidth = async () => {
    try {
      return await withTimeout(
        (async () => {
          const testUrl =
            "https://raw.githubusercontent.com/mdn/content/main/files/en-us/_wikihistory.json";
 
          const startTime = performance.now();
          const response = await fetch(testUrl, { cache: "no-store" });
          const blob = await response.blob();
          const endTime = performance.now();
 
          const bits = blob.size * 8;
          const sec = (endTime - startTime) / 1000;
          const speedMbps = (bits / sec / 1024 / 1024).toFixed(2);
 
          if (Number(speedMbps) > 0.5) {
            setPermissions((prev) => ({ ...prev, bandwidth: true }));
            showSnackbar(`Bandwidth OK (${speedMbps} Mbps)`, "success");
            return true;
          }
 
          showSnackbar(
            `Low internet speed detected (${speedMbps} Mbps).`,
            "warning"
          );
          return false;
        })(),
        30000
      );
    } catch {
      showSnackbar("Bandwidth check timed out", "error");
      setPermissions((prev) => ({ ...prev, bandwidth: false }));
      return false;
    }
  };
 
  const checkLocation = async () => {
    try {
      return await withTimeout(
        (async () => {
          const result = await geolocationService.checkUserLocation();
          if (result.is_indian) {
            setPermissions((prev) => ({ ...prev, location: true }));
            showSnackbar("location check passed", "success");
            return true;
          }
          return false;
        })(),
        30000
      );
    } catch (error) {
      setPermissions((prev) => ({ ...prev, location: false }));
      setLocationDialog(true);
      setLocationCheck({ loading: false, result: null, error: error.message });
      return false;
    }
  };
 
  const hostingProviders = [
    "aws",
    "amazon",
    "google",
    "gcp",
    "gce",
    "azure",
    "microsoft",
    "digitalocean",
    "linode",
    "vultr",
    "ovh",
    "contabo",
    "hetzner",
    "data center",
    "datacenter",
    "hosting",
  ];
 
  const mobileIsps = [
    "jio",
    "reliance",
    "airtel",
    "vodafone",
    "vi",
    "bsnl",
    "idea",
    "geo",
    "t-mobile",
    "vodafone",
    "orange",
    "o2",
  ];
 
  const checkVPN = async () => {
    try {
      return await withTimeout(
        (async () => {
          const res = await fetch(
            "https://ipwho.is/?fields=connection,isp,org,asn,timezone",
            {
              cache: "no-store",
            }
          );
 
          const data = await res.json();
          if (!data || !data.connection) {
            throw new Error("Invalid IP response");
          }
 
          const org = (data.org || "").toLowerCase();
          const isp = (data.isp || "").toLowerCase();
          const asnName = (data.asn?.name || "").toLowerCase();
          const timezone = data.timezone?.id || "";
 
          // HARD BLOCK
          if (
            data.connection.is_vpn ||
            data.connection.is_proxy ||
            data.connection.is_tor
          ) {
            setPermissions((prev) => ({ ...prev, vpn: false }));
            showSnackbar("VPN detected. Please disable it.", "warning");
            return false;
          }
 
          // Hosting networks
          if (
            [org, isp, asnName].some((i) =>
              hostingProviders.some((h) => i.includes(h))
            )
          ) {
            setPermissions((prev) => ({ ...prev, vpn: false }));
            showSnackbar("Hosting cloud network detected.", "error");
            return false;
          }
 
          // Mobile ISP allowed
          if (
            [org, isp].some((i) => mobileIsps.some((m) => i.includes(m)))
          ) {
            setPermissions((prev) => ({ ...prev, vpn: true }));
            showSnackbar("Mobile/CGNAT network detected → Allowed", "success");
            return true;
          }
 
          setPermissions((prev) => ({ ...prev, vpn: true }));
          showSnackbar("VPN check passed", "success");
          return true;
        })(),
        30000
      );
    } catch (err) {
      console.error("VPN Check Error:", err);
      setPermissions((prev) => ({ ...prev, vpn: false }));
      showSnackbar("Unable to verify VPN status.", "error");
      return false;
    }
  };
 
  const isPermissionDenied = async (name) => {
    try {
      const result = await navigator.permissions.query({ name });
      return result.state === "denied";
    } catch {
      return false;
    }
  };
 
  const handleCheck = async (key) => {
    setLoadingKey(key);
 
    try {
      let ok = false;
 
      if (key === "camera") {
        const denied = await isPermissionDenied("camera");
        if (denied) {
          setPermissionDialog({ open: true, type: "camera" });
          return false;
        }
        await navigator.mediaDevices.getUserMedia({ video: true });
        showSnackbar(`${key} access granted`, "success");
        ok = true;
      } else if (key === "mic") {
        const denied = await isPermissionDenied("microphone");
        if (denied) {
          setPermissionDialog({ open: true, type: "mic" });
          return false;
        }
        await navigator.mediaDevices.getUserMedia({ audio: true });
        showSnackbar(`${key} access granted`, "success");
        ok = true;
      } else if (key === "location") {
        ok = await checkLocation();
      } else if (key === "bandwidth") {
        ok = await checkBandwidth();
      } else if (key === "vpn") {
        ok = await checkVPN();
      }
 
      setPermissions((prev) => ({ ...prev, [key]: ok }));
      setChecked((prev) => ({ ...prev, [key]: true }));
      return ok;
    } catch {
      setPermissions((prev) => ({ ...prev, [key]: false }));
      setChecked((prev) => ({ ...prev, [key]: true }));
      showSnackbar(`Please allow ${key} access to continue.`, "error");
      return false;
    } finally {
      setLoadingKey(null);
    }
  };
 
  const handleAllowAll = async () => {
    setLoadingKey("all");
    const order = ["camera", "mic", "bandwidth", "vpn", "location"];
    for (const key of order) {
      await handleCheck(key);
    }
    setLoadingKey(null);
  };
 
  const requiredKeys = ["camera", "mic", "location"];
  const allGranted = requiredKeys.every((k) => permissions[k] === true);
 
  const handleNext = async (session) => {
    try {
      setLoadingKey("next");
      const payload = {
        session,
        session_metadata: { permissions, acknowledgement_signed: agree },
      };
 
      const response = await KycUserService.createSession(payload, session);
      if (response.status === 200 || response.status === 201) {
        localStorage.setItem("session_id", response?.data?.data?.session_id);
        onPermissionsGranted();
      } else throw new Error();
    } catch {
      showSnackbar("Error creating session", "error");
    } finally {
      setLoadingKey(null);
    }
  };
 
  const permissionItems = [
    {
      key: "camera",
      label: "Camera Access",
      icon: <CameraAltIcon fontSize="small" />,
    },
    {
      key: "mic",
      label: "Microphone Access",
      icon: <MicIcon fontSize="small" />,
    },
    {
      key: "bandwidth",
      label: "Bandwidth Check",
      icon: <WifiIcon fontSize="small" />,
    },
    {
      key: "vpn",
      label: "VPN Check",
      icon: <SecurityIcon fontSize="small" />,
    },
    {
      key: "location",
      label: "Location Check",
      icon: <LocationOnIcon fontSize="small" />,
    },
  ];
 
  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* LOCATION POPUP */}
      <Dialog
        open={locationDialog}
        onClose={() => setLocationDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background:
              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <LocationOnIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h6">Location Verification</Typography>
        </DialogTitle>
 
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          {locationCheck.result &&
            !locationCheck.result.is_indian && (
              <Box>
                <ErrorOutlineIcon
                  sx={{ fontSize: 60, color: "#f44336", mb: 2 }}
                />
                <Typography variant="h6" gutterBottom>
                  ❌ Access Restricted
                </Typography>
 
                <Alert
                  severity="error"
                  sx={{
                    mt: 2,
                    backgroundColor:
                      "rgba(244, 67, 54, 0.1)",
                    color: "white",
                    "& .MuiAlert-icon": { color: "white" },
                  }}
                >
                  User is from outside India (
                  {locationCheck.result.country || "Unknown"})
                </Alert>
              </Box>
            )}
 
          {locationCheck.error && (
            <Box>
              <ErrorOutlineIcon
                sx={{ fontSize: 60, color: "#ff9800", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                ⚠️ Verification Failed
              </Typography>
 
              <Alert
                severity="warning"
                sx={{
                  mt: 2,
                  backgroundColor:
                    "rgba(255, 152, 0, 0.1)",
                  color: "white",
                  "& .MuiAlert-icon": { color: "white" },
                }}
              >
                Unable to verify location:{" "}
                {locationCheck.error}
              </Alert>
            </Box>
          )}
        </DialogContent>
 
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            onClick={() => setLocationDialog(false)}
            variant="outlined"
            sx={{
              color: "white",
              borderColor: "white",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.7)",
                backgroundColor:
                  "rgba(255,255,255,0.1)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
 
      {/* LEFT SIDE */}
      <Box
        sx={{
          flex: 1,
          background:
            "radial-gradient(circle at center, #0b65c2, #00356b)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: isMobile ? 3 : 0,
        }}
      >
        <Box
          component="img"
          src={Permission}
          alt="HabileLabs"
          sx={{ width: { xs: 250, sm: 300, md: 450 } }}
        />
      </Box>
 
      {/* RIGHT SIDE */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#ffffff",
          p: { xs: 1, sm: 2, md: 0 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 500,
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            mb={0.5}
            sx={{
              fontSize: {
                xs: "1.2rem",
                md: "1.5rem",
              },
            }}
          >
            Welcome to Intelli VKYC
          </Typography>
 
          <Typography
            variant="body2"
            color="text.secondary"
            mb={3}
          >
            Verifying your identity and securely
            recording your consent
          </Typography>
 
          <Typography
            variant="subtitle2"
            fontWeight={600}
            mb={1}
          >
            PERMISSION CHECK
          </Typography>
 
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              mb: 0.5,
            }}
          >
            {permissionItems.map((item) => (
              <Paper
                key={item.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 0.3,
                  border: "1px solid #e0e0e0",
                  borderRadius: "10px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
 
                  <Box>
                    <Typography
                      fontWeight={500}
                      fontSize="0.9rem"
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#9e9e9e" }}
                    >
                      Check permission
                    </Typography>
                  </Box>
                </Box>
 
                {loadingKey === item.key ? (
                  <CircularProgress size={18} />
                ) : checked[item.key] &&
                  permissions[item.key] === true ? (
                  <CheckCircleIcon
                    color="success"
                    fontSize="small"
                  />
                ) : checked[item.key] &&
                  permissions[item.key] === false ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <WarningAmberIcon
                      sx={{ color: "#FFA000" }}
                      fontSize="small"
                    />
 
                    <ReplayIcon
                      onClick={() =>
                        handleCheck(item.key)
                      }
                      sx={{
                        fontSize: 20,
                        color: "#1976d2",
                        cursor: "pointer",
                        "&:hover": {
                          color: "#0d47a1",
                        },
                      }}
                    />
                  </Box>
                ) : (
                  <Button
                    variant="text"
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 500,
                      color: "#1976d2",
                      fontSize: "0.85rem",
                    }}
                    onClick={() => handleCheck(item.key)}
                  >
                    check
                  </Button>
                )}
              </Paper>
            ))}
          </Box>
 
          <Button
            onClick={handleAllowAll}
            variant="text"
            sx={{
              color: "#1976d2",
              fontWeight: 500,
              textTransform: "none",
              fontSize: "0.9rem",
              mb: 1,
            }}
            disabled={loadingKey !== null}
          >
            {loadingKey === "all" ? (
              <CircularProgress size={18} />
            ) : (
              "Allow all permissions at once"
            )}
          </Button>
 
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Checkbox
              checked={agree}
              onChange={(e) =>
                setAgree(e.target.checked)
              }
              color="primary"
              sx={{
                "& .MuiSvgIcon-root": { fontSize: 22 },
                mt: "-8px",
              }}
            />
 
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.8rem",
                color: "#555",
                lineHeight: 1.4,
              }}
            >
              I agree to proceed with the Intelli
              V-KYC process and authorize the
              recording of this session.
            </Typography>
          </Box>
 
          <Button
            variant="contained"
            fullWidth
            disabled={!allGranted || !agree}
            onClick={() => handleNext(session)}
            sx={{
              mt: 2,
              py: 1.3,
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "0.95rem",
              backgroundColor:
                allGranted && agree
                  ? "#1976d2"
                  : "#cfd8dc",
              color: "#fff",
              borderRadius: "8px",
              mb: 2,
            }}
          >
            {loadingKey === "next" ? (
              <CircularProgress
                size={22}
                color="inherit"
              />
            ) : (
              "Next"
            )}
          </Button>
        </Paper>
      </Box>
 
      {/* SNACKBAR */}
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
 
      <Dialog
        open={permissionDialog.open}
        onClose={() => setPermissionDialog({ open: false, type: null })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 42, color: "#f9a825", mb: 1 }} />
          <Typography variant="h6" fontWeight={700}>
            Permission Required
          </Typography>
        </DialogTitle>
 
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ textAlign: "center", color: "text.secondary", mb: 3 }}
          >
            {permissionDialog.type === "camera"
              ? "Camera access is currently blocked by your browser or device."
              : "Microphone access is currently blocked by your browser or device."}
          </Typography>
 
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              p: 2.5,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography fontWeight={600} mb={1}>
              How to enable access
            </Typography>
 
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.2,
                fontSize: "0.9rem",
                color: "#444",
              }}
            >
              <Typography>
                1. Open your browser or device <b>Settings</b>
              </Typography>
 
              <Typography>
                2. Go to <b>Privacy</b> or <b>Site Permissions</b>
              </Typography>
 
              <Typography>
                3. Allow <b>{permissionDialog.type}</b> access for this website
              </Typography>
 
              <Typography>
                4. Refresh this page and try again
              </Typography>
            </Box>
          </Paper>
 
          <Alert
            severity="info"
            sx={{
              mt: 2,
              fontSize: "0.8rem",
              backgroundColor: "#e3f2fd",
            }}
          >
            For security reasons, websites cannot change permission settings
            automatically.
          </Alert>
        </DialogContent>
 
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button
            onClick={() => setPermissionDialog({ open: false, type: null })}
            variant="outlined"
            sx={{ textTransform: "none", px: 4 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
 
export default PermissionCheckScreen;